import { TILE_HALF_H, TILE_HALF_W, Z_UNIT_PX } from '@enugu/shared';
/**
 * Tile -> world-screen projection, exactly per vision §6:
 *   screenX = (tx - ty) * 32
 *   screenY = (tx + ty) * 16 - z * 32
 * "World-screen" px are logical @1x px before the camera transform.
 */
export function tileToScreen(tx, ty, z, out) {
    out.x = (tx - ty) * TILE_HALF_W;
    out.y = (tx + ty) * TILE_HALF_H - z * Z_UNIT_PX;
    return out;
}
/**
 * Inverse projection: world-screen px -> float tile coords on the plane at
 * height `z` (z = 0 is the ground floor).
 *   tx - ty = sx / 32
 *   tx + ty = (sy + z * 32) / 16
 */
export function screenToTile(sx, sy, z, out) {
    const a = sx / TILE_HALF_W;
    const b = (sy + z * Z_UNIT_PX) / TILE_HALF_H;
    out.x = (a + b) / 2;
    out.y = (b - a) / 2;
    return out;
}
/** Screen offset of a footprint's anchor (bottom-centre of its bounding box) relative to its top corner tile (x, y). */
export function footprintAnchorOffset(w, h, out) {
    out.x = (w - h) * (TILE_HALF_W / 2);
    out.y = (w + h) * TILE_HALF_H;
    return out;
}
const DIAG_EPS = 1e-6;
/**
 * Picks the closest of the 4 facings for a heading (dx, dy) in tile space.
 * Diagonal moves are exact ties between two facings; we keep `prev` if it is
 * one of them (no flip-flopping while zig-zagging), otherwise we prefer the
 * camera-facing (south) one so faces stay readable.
 */
export function headingToDirection(dx, dy, prev = 'SE') {
    const ax = Math.abs(dx);
    const ay = Math.abs(dy);
    if (ax < DIAG_EPS && ay < DIAG_EPS)
        return prev;
    if (ax > ay + DIAG_EPS)
        return dx > 0 ? 'SE' : 'NW';
    if (ay > ax + DIAG_EPS)
        return dy > 0 ? 'SW' : 'NE';
    const a = dx > 0 ? 'SE' : 'NW';
    const b = dy > 0 ? 'SW' : 'NE';
    if (prev === a || prev === b)
        return prev;
    if (a === 'SE')
        return 'SE'; // (+,+) and (+,-): SE is front-facing
    if (b === 'SW')
        return 'SW'; // (-,+)
    return 'NE'; // (-,-): both are back-facing; NE is authored (not mirrored)
}
/** SW and NW are drawn as horizontal mirrors of SE and NE. */
export function isMirrored(dir) {
    return dir === 'SW' || dir === 'NW';
}
/** Which authored direction a facing uses. */
export function authoredDirection(dir) {
    return dir === 'SE' || dir === 'SW' ? 'SE' : 'NE';
}
